import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';
import { formatTimeSlot } from '@/utils/transformer';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';

interface TimeSlotWheelPickerProps {
	onValueChange?: (value: string) => void;
	slots?: Array<AvailableDates.SessionTimeSlot>;
	disabled?: boolean;
	defaultValue?: AvailableDates.SessionTimeSlot | null;
	compact?: boolean;
}

const slotToValue = (slot: AvailableDates.SessionTimeSlot) =>
	JSON.stringify(slot);

const formatSlotStartLabel = (slot: AvailableDates.SessionTimeSlot) => {
	const start = new Date(slot.start_time);
	const hours = start.getHours().toString().padStart(2, '0');
	const minutes = start.getMinutes().toString().padStart(2, '0');
	return `${hours}:${minutes}`;
};

const wheelButtonClassName = cn(
	'flex h-10 w-full items-center justify-center rounded-xl border border-dashed border-zinc-300',
	'bg-white text-xs font-semibold text-zinc-700 transition-colors',
	'hover:border-zinc-400 hover:bg-zinc-50',
	'disabled:cursor-not-allowed disabled:border-zinc-200 disabled:text-zinc-300 disabled:hover:bg-white',
);

export const TimeSlotWheelPicker = ({
	onValueChange,
	slots = [],
	disabled = false,
	defaultValue = null,
	compact = false,
}: TimeSlotWheelPickerProps) => {
	const [selectedValue, setSelectedValue] = useState<string | null>(null);

	const sortedSlots = useMemo(
		() =>
			[...slots].sort(
				(a, b) =>
					new Date(a.start_time).getTime() -
					new Date(b.start_time).getTime(),
			),
		[slots],
	);

	const selectedLabel = useMemo(() => {
		if (!selectedValue) return null;

		try {
			const parsed = JSON.parse(
				selectedValue,
			) as AvailableDates.SessionTimeSlot;
			return formatTimeSlot(parsed.start_time, parsed.end_time);
		} catch {
			return null;
		}
	}, [selectedValue]);

	const currentIndex = useMemo(() => {
		if (!selectedValue) return -1;
		return sortedSlots.findIndex(
			(slot) => slotToValue(slot) === selectedValue,
		);
	}, [selectedValue, sortedSlots]);

	const applySlot = (slot: AvailableDates.SessionTimeSlot) => {
		const value = slotToValue(slot);
		setSelectedValue(value);
		onValueChange?.(value);
	};

	useEffect(() => {
		setSelectedValue(null);
	}, [slots]);

	useEffect(() => {
		if (selectedValue || !defaultValue || sortedSlots.length === 0) return;

		const defaultSerialized = slotToValue(defaultValue);
		const hasDefault = sortedSlots.some(
			(slot) => slotToValue(slot) === defaultSerialized,
		);

		if (hasDefault) setSelectedValue(defaultSerialized);
	}, [defaultValue, selectedValue, sortedSlots]);

	const shiftHour = (delta: number) => {
		const baseIndex = currentIndex >= 0 ? currentIndex : 0;
		const nextIndex = baseIndex + delta;

		if (nextIndex < 0 || nextIndex >= sortedSlots.length) return;

		applySlot(sortedSlots[nextIndex]);
	};

	const wheelWidthClass = compact ? 'max-w-[7.5rem]' : 'max-w-[10rem]';
	const wheelButtonSizeClass = compact ? 'h-8' : 'h-10';
	const wheelSelectSizeClass = compact ? 'h-9 text-xs' : 'h-11 text-sm';
	const wheelIconSizeClass = compact ? 'size-4' : 'size-5';
	const wheelGapClass = compact ? 'gap-1.5' : 'gap-2';
	const hasSlots = sortedSlots.length > 0;

	if (!hasSlots || disabled) {
		return (
			<div
				className={cn(
					'mx-auto flex w-full items-center justify-center rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-3 text-xs font-medium text-zinc-400',
					wheelWidthClass,
				)}
			>
				Sem horário disponível
			</div>
		);
	}

	const canDecrease = currentIndex > 0;
	const canIncrease =
		currentIndex >= 0 && currentIndex < sortedSlots.length - 1;

	return (
		<div
			className={cn(
				'mx-auto flex w-full flex-col items-stretch',
				wheelWidthClass,
				wheelGapClass,
			)}
		>
			<button
				type="button"
				aria-label="Horário anterior"
				onClick={() => shiftHour(-1)}
				disabled={!canDecrease}
				className={cn(wheelButtonClassName, wheelButtonSizeClass)}
			>
				<ChevronUp className={wheelIconSizeClass} strokeWidth={2.25} />
			</button>

			<Select
				value={selectedValue ?? ''}
				onValueChange={(value) => {
					setSelectedValue(value);
					onValueChange?.(value);
				}}
			>
				<SelectTrigger
					data-testid={E2E_SCHEDULING.TIME_SLOT_PREFIX + 'wheel'}
					className={cn(
						'w-full rounded-xl border border-zinc-300 bg-white px-3 font-semibold text-zinc-800 shadow-sm focus:ring-2 focus:ring-stone-950/10',
						wheelSelectSizeClass,
					)}
				>
					<SelectValue>{selectedLabel ?? 'Horário'}</SelectValue>
				</SelectTrigger>
				<SelectContent className="max-h-60 min-w-[8.5rem]">
					<SelectGroup>
						{sortedSlots.map((slot, index) => (
							<SelectItem
								key={`${slot.start_time}-${index}`}
								value={slotToValue(slot)}
								data-testid={`${E2E_SCHEDULING.TIME_SLOT_PREFIX}${index}`}
								className="cursor-pointer justify-center"
							>
								{formatSlotStartLabel(slot)}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>

			<button
				type="button"
				aria-label="Próximo horário"
				onClick={() => shiftHour(1)}
				disabled={!canIncrease}
				className={cn(wheelButtonClassName, wheelButtonSizeClass)}
			>
				<ChevronDown
					className={wheelIconSizeClass}
					strokeWidth={2.25}
				/>
			</button>
		</div>
	);
};
